// src/components/features/timetables/Grid/TimetableGrid.tsx
import { useState } from 'preact/hooks';
// Types
import type { TimetableNotice } from '../../../../arcticapi/types';
import type { ComponentProps } from 'preact';
import type {
  TimetableJourney as TimetableGridJourney,
  TimetableTableRow as TimetableGridRow,
} from '../../../../data/useTimetable';
// Components
import { SvgIcon } from '../../../base/Icon/SvgIcon';
import { TimetableCell } from './Cell';
import { noteAnchorId, replayNoteHighlight } from '../Notes/TimetableNotes';
// Styles
import styles from './TimetableGrid.module.css';

export type { TimetableGridJourney, TimetableGridRow };

export interface TimetableGridProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  waypoints: TimetableGridRow[];
  journeys: TimetableGridJourney[];
  notices?: TimetableNotice[];
  onShowStopDeparturesDetails?: (waypoint: TimetableGridRow) => void;
}

function formatStopLabel(waypoint: TimetableGridRow): string {
  const indicator = waypoint.indicator?.trim();
  const localityName = waypoint.localityName?.trim();
  const nameWithIndicator = indicator
    ? `${waypoint.label} (${indicator})`
    : waypoint.label;

  return localityName
    ? `${nameWithIndicator}, ${localityName}`
    : nameWithIndicator;
}

function renderStopLabel(waypoint: TimetableGridRow) {
  const indicator = waypoint.indicator?.trim();
  const localityName = waypoint.localityName?.trim();
  const primaryLabel = indicator
    ? `${waypoint.label} (${indicator})`
    : waypoint.label;

  return (
    <>
      <span className={styles.stopPrimary}>{primaryLabel}</span>
      {localityName ? (
        <span className={styles.stopLocality}>{`, ${localityName}`}</span>
      ) : null}
    </>
  );
}

function getJourneyNotices(
  journey: TimetableGridJourney,
  notices: TimetableNotice[],
): TimetableNotice[] {
  return (journey.noticeCodes ?? [])
    .map((code) => notices.find((notice) => notice.noticeCode === code))
    .filter((notice): notice is TimetableNotice => notice !== undefined);
}

/** Two codes fit side by side in a column; three or more stack instead. */
const JOURNEY_NOTE_STACK_THRESHOLD = 3;

/**
 * The codes a journey's notes carry, below its line name.
 *
 * Each code links to its definition in the notes list under the timetable,
 * so a reader can look one up without leaving the grid. Native anchors do the
 * scrolling and focusing; no click handler is needed.
 */
function JourneyNoteCodes({
  journeyNotices,
}: {
  journeyNotices: TimetableNotice[];
}) {
  if (journeyNotices.length === 0) {
    return null;
  }

  const className = [
    styles.noteCodes,
    journeyNotices.length >= JOURNEY_NOTE_STACK_THRESHOLD
      ? styles.noteCodesStacked
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={className}>
      {journeyNotices.map((notice) => (
        <a
          key={notice.noticeCode}
          href={`#${noteAnchorId(notice.noticeCode)}`}
          className={styles.noteCode}
          aria-label={`${notice.noticeCode}: ${notice.noticeText}`}
          onClick={() => {
            // Clicking the already-targeted code changes nothing about the
            // URL, so `:target` won't re-fire its highlight on its own.
            if (
              window.location.hash === `#${noteAnchorId(notice.noticeCode)}`
            ) {
              replayNoteHighlight(notice.noticeCode);
            }
          }}
        >
          {`[${notice.noticeCode}]`}
        </a>
      ))}
    </span>
  );
}

/**
 * Displays the tabular timetable results.
 *
 * Intended for headers, rows and columns of stops and scheduled times.
 *
 * Each row has a Waypoint header on the left. Waypoints link to the stop's departure board.
 * Each column has a Journey header at the top, with the line name and coloured with its colour.
 * A journey carrying notes gets a second header row of its own, listing the note codes below the
 * line names on the timetable's neutral surface, rather than each column's own line colour. The
 * intersection of a row and column contains the scheduled time for that stop on that journey.
 */
export function TimetableGrid({
  waypoints,
  journeys,
  notices = [],
  onShowStopDeparturesDetails,
  ...props
}: TimetableGridProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const stopColumnHeaderStyle = {
    backgroundColor:
      journeys[0]?.lineColors?.background ?? 'var(--widget-brand-secondary)',
    color: journeys[0]?.lineColors?.foreground ?? '#FFFFFF',
  };

  const journeyNotices = journeys.map((journey) =>
    getJourneyNotices(journey, notices),
  );
  const hasAnyJourneyNotices = journeyNotices.some(
    (notices) => notices.length > 0,
  );

  return (
    <div
      data-component="Timetable Grid"
      {...props}
      className={`${styles.wrapper} ${isExpanded ? styles.isExpanded : ''}`}
    >
      <table className={styles.table}>
        <thead>
          <tr>
            <th
              scope="col"
              className={styles.stopColumnHeader}
              style={stopColumnHeaderStyle}
            >
              <div className={styles.headerContent}>
                <button
                  title={isExpanded ? 'Collapse column' : 'Expand column'}
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setIsExpanded(!isExpanded)}
                  aria-label={isExpanded ? 'Collapse column' : 'Expand column'}
                >
                  <SvgIcon
                    name="doubleChevron"
                    size={24}
                    strokeWidth="1"
                    className={`${styles.chevronIcon} ${!isExpanded ? styles.isFlipped : ''}`}
                  />
                </button>
              </div>
            </th>
            {journeys.map((journey) => (
              <th
                key={journey.id}
                scope="col"
                className={styles.journeyHeader}
                style={{
                  backgroundColor:
                    journey.lineColors?.background ??
                    stopColumnHeaderStyle.backgroundColor,
                  color:
                    journey.lineColors?.foreground ??
                    stopColumnHeaderStyle.color,
                }}
              >
                <span className={styles.journeyLineName}>
                  {journey.lineName}
                </span>
              </th>
            ))}
          </tr>
          {hasAnyJourneyNotices ? (
            <tr className={styles.noteRow}>
              <th className={styles.noteRowStopCell} aria-hidden="true" />
              {journeys.map((journey, journeyIndex) => (
                <th
                  key={`${journey.id}-notes`}
                  scope="col"
                  className={styles.noteRowCell}
                >
                  <JourneyNoteCodes
                    journeyNotices={journeyNotices[journeyIndex]}
                  />
                </th>
              ))}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {waypoints.map((waypoint, waypointIndex) => (
            <tr key={waypoint.id}>
              <th scope="row" className={styles.stopRowHeader}>
                {waypoint.stopAtcoCode && onShowStopDeparturesDetails ? (
                  <button
                    type="button"
                    className={`${styles.stopLabel} ${styles.stopLink}`}
                    onClick={() => onShowStopDeparturesDetails(waypoint)}
                    aria-label={`View departures for ${formatStopLabel(waypoint)}`}
                  >
                    {renderStopLabel(waypoint)}
                  </button>
                ) : (
                  <span className={styles.stopLabel}>
                    {renderStopLabel(waypoint)}
                  </span>
                )}
              </th>
              {journeys.map((journey) => (
                <td
                  key={`${waypoint.id}-${journey.id}`}
                  className={styles.timeCell}
                >
                  <TimetableCell cell={journey.waypointCells[waypointIndex]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
